class MyBrilintonDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.panel = this.querySelector('.my-brilinton-drawer__panel');
    this.closeButtons = this.querySelectorAll('[data-my-brilinton-close]');
    this.defaultAuth = this.querySelector('[data-my-brilinton-default-auth]');
    this.offer = this.querySelector('[data-my-brilinton-offer]');
    this.klaviyoMount = this.querySelector('[data-my-brilinton-klaviyo-mount]');
    this.benefitActive = this.querySelector('[data-my-brilinton-benefit-active]');
    this.benefitDays = Math.max(1, Number.parseInt(this.dataset.benefitDays || '14', 10) || 14);
    this.submittedThisPage = false;
    this.benefitRestored = false;
    this.couponWaitUntil = 0;
    this.offerEligible = false;
    this.formViewTracked = false;
    this.formId = (this.dataset.klaviyoFormId || '').trim();
    this.noticeBadges = Array.from(document.querySelectorAll('[data-my-brilinton-badge]'));
    this.authLinks = Array.from(this.querySelectorAll('[data-my-brilinton-auth-link]'));
    this.prospectSection = this.querySelector('[data-my-brilinton-prospect]');
    this.prospectProducts = this.querySelector('[data-my-brilinton-prospect-products]');
    this.prospectRenderSignature = '';
    this.prospectFlickity = null;
    this.prospectFlickityLoading = false;
    this.notification = document.querySelector('[data-my-brilinton-notification]');
    this.notificationOpen = this.notification?.querySelector('[data-my-brilinton-notification-open]') || null;
    this.notificationClose = this.notification?.querySelector('[data-my-brilinton-notification-close]') || null;
    this.prospectEnabled = this.dataset.prospectEnabled === 'true';
    this.prospectMaxProducts = Math.min(4, Math.max(2, Number.parseInt(this.dataset.prospectProducts || '4', 10) || 4));
    this.prospectReturnDelay = Math.max(0, Number.parseInt(this.dataset.prospectReturnDelay || '0', 10) || 0);
    this.prospectEngagedSeconds = Math.max(8, Number.parseInt(this.dataset.prospectEngagedSeconds || '12', 10) || 12);
    this.prospectNotificationSeconds = Math.max(15, Number.parseInt(this.dataset.prospectNotificationSeconds || '15', 10) || 15);
    this.prospectDismissDays = Math.max(1, Number.parseInt(this.dataset.prospectDismissDays || '14', 10) || 14);
    this.prospectState = null;
    this.prospectSessionStartedAt = Date.now();
    this.prospectVisibleSeconds = 0;
    this.prospectMaxScrollDepth = 0;
    this.prospectInteractionCount = 0;
    this.prospectMeaningfulCurrent = false;
    this.prospectPromptActive = false;
    this.prospectReturnCandidate = false;
    this.prospectDriftCandidate = false;
    this.prospectIsReturningSession = false;
    this.prospectPreviousPage = null;
    this.prospectInterval = null;
    this.notificationExposureMs = 0;
    this.notificationLastTick = 0;
    this.notificationActions = 0;
    this.notificationLastActionAt = 0;
    this.notificationStartY = 0;
    this.notificationScrollDistance = 0;
    this.prospectCartUnsubscriber = null;
    this.handleKeyup = this.handleKeyup.bind(this);
    this.handleKlaviyoEvent = this.handleKlaviyoEvent.bind(this);
    this.close = this.close.bind(this);
    this.updateMobileOffset = this.updateMobileOffset.bind(this);
    this.handleProspectScroll = this.handleProspectScroll.bind(this);
    this.handleProspectInteraction = this.handleProspectInteraction.bind(this);
    this.handleSurfaceOpen = this.handleSurfaceOpen.bind(this);
    this.handleSurfaceClose = this.handleSurfaceClose.bind(this);
    this.handleProspectNotificationOpen = this.handleProspectNotificationOpen.bind(this);
    this.handleProspectNotificationClose = this.handleProspectNotificationClose.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleModalKeydown = this.handleModalKeydown.bind(this);
    this.syncProspectSliderFocus = this.syncProspectSliderFocus.bind(this);
    this.handleProspectCardFocus = this.handleProspectCardFocus.bind(this);
  }

  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.triggers = Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
    this.setNoticeBadge(false);
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.classList.contains('active') ? this.close() : this.open(trigger);
      });
    });

    this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
    this.notificationOpen?.addEventListener('click', this.handleProspectNotificationOpen);
    this.notificationClose?.addEventListener('click', this.handleProspectNotificationClose);
    // Keep the notification fixed to the viewport, outside the sticky header's
    // transform/overflow context. Scrolling the header must not dismiss it.
    if (this.notification) document.body.appendChild(this.notification);
    this.querySelectorAll('[data-klaviyo-chat-open]').forEach((link) => this.bindKlaviyoChatLink(link));
    this.addEventListener('keyup', this.handleKeyup);
    this.prospectProducts?.addEventListener('focusin', this.handleProspectCardFocus);
    window.addEventListener('resize', this.updateMobileOffset, { passive: true });
    window.addEventListener('klaviyoForms', this.handleKlaviyoEvent);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('pagehide', this.handlePageHide);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.prepareAuthenticationLinks();
    this.updateMobileOffset();
    this.restoreBenefitState();
    this.observeKlaviyoMount();
    this.initializeProspectCapture();
    this.reopenAfterAuthentication();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateMobileOffset);
    window.removeEventListener('klaviyoForms', this.handleKlaviyoEvent);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('pagehide', this.handlePageHide);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('keydown', this.handleModalKeydown, true);
    window.removeEventListener('scroll', this.handleProspectScroll);
    document.removeEventListener('click', this.handleProspectInteraction, true);
    document.removeEventListener('change', this.handleProspectInteraction, true);
    document.removeEventListener('brilinton:surface-open', this.handleSurfaceOpen);
    document.removeEventListener('brilinton:surface-close', this.handleSurfaceClose);
    this.notificationOpen?.removeEventListener('click', this.handleProspectNotificationOpen);
    this.notificationClose?.removeEventListener('click', this.handleProspectNotificationClose);
    this.prospectProducts?.removeEventListener('focusin', this.handleProspectCardFocus);
    this.destroyProspectSlider();
    if (this.prospectInterval) window.clearInterval(this.prospectInterval);
    this.notification?.remove();
    if (typeof this.prospectCartUnsubscriber === 'function') this.prospectCartUnsubscriber();
    if (this.klaviyoObserver) this.klaviyoObserver.disconnect();
  }


  setNoticeBadge(visible) {
    const shouldShow = Boolean(visible);

    this.noticeBadges.forEach((badge) => {
      badge.hidden = !shouldShow;
    });
  }

  prepareAuthenticationLinks() {
    if (!this.authLinks.length) return;

    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set('myb', 'open');
    const returnTo = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
    this.dataset.returnTo = returnTo;

    this.authLinks.forEach((link) => {
      const loginUrl = new URL('/customer_authentication/login', window.location.origin);
      loginUrl.searchParams.set('return_to', returnTo);
      link.href = `${loginUrl.pathname}${loginUrl.search}`;
    });
  }

  updateMobileOffset() {
    const headerClose = this.mobileCloseControl();
    this.classList.toggle('has-header-close', Boolean(headerClose));
    if (this.classList.contains('active') && headerClose?.id) this.panel?.setAttribute('aria-owns', headerClose.id);
    else this.panel?.removeAttribute('aria-owns');
    if (!window.matchMedia('(max-width: 749px)').matches) {
      this.style.removeProperty('--myb-drawer-top');
    } else {
      const headerWrapper = document.querySelector('.header-wrapper');
      const top = headerWrapper ? Math.max(0, Math.round(headerWrapper.getBoundingClientRect().bottom)) : 0;
      this.style.setProperty('--myb-drawer-top', `${top}px`);
    }
    this.updateNotificationOffset();
    this.refreshProspectSlider();
  }

  mobileCloseControl() {
    if (!window.matchMedia('(max-width: 749px)').matches) return null;
    const control = document.querySelector('#hamburger-button, header-drawer .hamburger');
    return control?.getClientRects().length ? control : null;
  }

  updateNotificationOffset() {
    if (!this.notification) return;
    const header = document.querySelector('.header-wrapper');
    const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.notification.style.setProperty('--myb-notification-top', `${bottom}px`);
  }

  modalFocusableElements() {
    const elements = Array.from(this.panel?.querySelectorAll('a[href], button:not(:disabled), input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') || []);
    const headerClose = this.mobileCloseControl();
    if (headerClose) elements.unshift(headerClose);
    return elements.filter((element) => element.getAttribute('tabindex') !== '-1' && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden' && !element.closest('[hidden], [inert]'));
  }

  handleModalKeydown(event) {
    if (!this.classList.contains('active')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const elements = this.modalFocusableElements();
    if (!elements.length) return;
    const index = elements.indexOf(document.activeElement);
    const nextIndex = index < 0 ? (event.shiftKey ? elements.length - 1 : 0) : (index + (event.shiftKey ? -1 : 1) + elements.length) % elements.length;
    event.preventDefault();
    const next = elements[nextIndex];
    next.focus({ preventScroll: next === this.mobileCloseControl() });
  }

  setHamburgerState(active) {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const hamburger = document.querySelector('#hamburger-button, header-drawer .hamburger');
    if (!hamburger) return;

    hamburger.classList.toggle('active', active);
    hamburger.setAttribute('aria-label', active ? 'Close My Brilinton' : 'Menu');
  }

  open(trigger, options = {}) {
    this.updateMobileOffset();
    const acquisitionReason = options.reason === 'prospect' || options.reason === 'notification';
    this.prospectPromptActive = options.reason === 'prospect';
    this.classList.toggle('is-prospect-mode', acquisitionReason);
    if (!this.prospectPromptActive && this.prospectEnabled) this.setSessionValue(this.prospectPromptedKey(), '1');
    this.renderProspectRecommendations();
    this.hideProspectNotification({ keepBadge: false });
    this.setNoticeBadge(false);
    if (window.BrilintonSurfaceManager) {
      window.BrilintonSurfaceManager.register('my-brilinton', {
        root: () => this,
        close: (surfaceOptions) => this.close(surfaceOptions),
      });
      window.BrilintonSurfaceManager.open('my-brilinton', {
        trigger: trigger || document.activeElement,
        lockMode: 'overflow',
      });
    }

    // The eligible form remains available until submission. After submission,
    // preserve a real Klaviyo coupon success step when one exists; otherwise use
    // the Brilinton-owned confirmation state instead of leaving stale form copy.
    if (this.hasActiveBenefit()) {
      this.syncBenefitPresentation();
    } else if (this.classList.contains('is-klaviyo-eligible')) {
      if (this.offer) this.offer.hidden = false;
      if (this.benefitActive) this.benefitActive.hidden = true;
      if (this.defaultAuth) this.defaultAuth.hidden = true;
    }

    this.activeElement = trigger || document.activeElement;
    this.classList.add('active');
    this.setAttribute('aria-hidden', 'false');
    if (!window.BrilintonSurfaceManager) document.body.classList.add('overflow-hidden');

    this.triggers.forEach((button) => {
      button.setAttribute('aria-expanded', 'true');
      button.classList.add('is-active');
    });

    this.setHamburgerState(true);
    this.updateMobileOffset();

    if (this.offerEligible && !this.hasActiveBenefit() && !this.formViewTracked) {
      this.formViewTracked = true;
      this.trackClarityEvent('member_benefit_form_viewed');
    }

    // Include the existing mobile header close in the modal's keyboard loop.
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    document.addEventListener('keydown', this.handleModalKeydown, true);
    requestAnimationFrame(() => {
      if (!this.classList.contains('active')) return;
      this.refreshProspectSlider();
      this.modalFocusableElements()[0]?.focus({ preventScroll: true });
    });
  }

  close(options = {}) {
    if (!this.classList.contains('active')) return;
    const closeReason = options && typeof options === 'object' && 'reason' in options ? options.reason : 'dismiss';
    const shouldSuppressProspect = this.prospectPromptActive
      && closeReason !== 'handoff'
      && !this.submittedThisPage
      && !this.hasActiveBenefit();

    this.classList.remove('active');
    this.panel?.removeAttribute('aria-owns');
    document.removeEventListener('keydown', this.handleModalKeydown, true);
    this.setAttribute('aria-hidden', 'true');

    this.triggers.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove('is-active');
    });

    if (window.BrilintonSurfaceManager) {
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      window.BrilintonSurfaceManager.close('my-brilinton', { restoreFocus: true });
      this.setHamburgerState(Boolean(window.BrilintonSurfaceManager.top()));
    } else {
      const menuOpen = document.querySelector('header-drawer details[open]');
      const cartOpen = document.querySelector('cart-drawer.active');
      if (!menuOpen && !cartOpen) {
        document.body.classList.remove('overflow-hidden');
        this.setHamburgerState(false);
      }

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeElement);
      } else if (this.activeElement && typeof this.activeElement.focus === 'function') {
        this.activeElement.focus();
      }
    }

    if (shouldSuppressProspect) this.suppressProspectPrompt();
    this.prospectPromptActive = false;
    this.classList.remove('is-prospect-mode');
  }

  bindKlaviyoChatLink(link) {
    link.addEventListener('click', (event) => {
      if (!window.customerHubApi || typeof window.customerHubApi.open !== 'function') return;

      event.preventDefault();
      this.close();
      window.setTimeout(() => window.customerHubApi.open('/chat'), 180);
    });
  }

  handleKlaviyoEvent(event) {
    if (!this.formId || !event?.detail) return;

    const eventFormId = String(event.detail.formId || event.detail.formID || '').trim();
    if (eventFormId.toLowerCase() !== this.formId.toLowerCase()) return;

    if (event.detail.type === 'embedOpen') {
      this.showEligibleOffer();
      return;
    }

    if (event.detail.type === 'submit') {
      this.activateBenefit();
    }
  }

  observeKlaviyoMount() {
    if (!this.formId || !this.klaviyoMount) return;

    const detect = () => {
      this.cleanKlaviyoAcquisitionCopy();
      if (this.hasActiveBenefit()) {
        this.syncBenefitPresentation();
        return;
      }

      const rendered = Boolean(
        this.klaviyoMount.querySelector('input, form, button, [data-testid*="form"]')
      );
      if (rendered) this.showEligibleOffer();
    };

    this.klaviyoObserver = new MutationObserver(detect);
    this.klaviyoObserver.observe(this.klaviyoMount, { childList: true, subtree: true, characterData: true });
    detect();
    window.setTimeout(detect, 600);
    window.setTimeout(detect, 1800);
  }

  cleanKlaviyoAcquisitionCopy() {
    if (!this.klaviyoMount) return;

    this.klaviyoMount.querySelectorAll('.klaviyo-form-richtext').forEach((block) => {
      const text = String(block.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (!text || text.length > 100) return;
      if (!text.includes('join my brilinton')) return;
      if (/(privacy|terms|consent|marketing|subscribe|sms|agree)/.test(text)) return;
      block.dataset.mybRedundantCopy = 'true';
      block.hidden = true;
    });
  }

  showEligibleOffer() {
    this.offerEligible = true;

    if (this.hasActiveBenefit() && !this.submittedThisPage) {
      this.showBenefitFallback();
      return;
    }

    if (!this.offer) return;
    this.offer.hidden = false;
    if (this.benefitActive) this.benefitActive.hidden = true;
    if (this.defaultAuth) this.defaultAuth.hidden = true;
    this.classList.add('is-klaviyo-eligible');
    this.evaluateProspectPrompt();

    if (this.classList.contains('active')) {
      if (!this.formViewTracked) {
        this.formViewTracked = true;
        this.trackClarityEvent('member_benefit_form_viewed');
      }
      return;
    }

  }


  trackClarityEvent(name) {
    if (!name) return;
    try {
      if (typeof window.clarity === 'function') window.clarity('event', name);
    } catch (_error) {}
  }

  prospectStorageKey() {
    return 'brilinton.myb.prospect.v1';
  }

  prospectSessionKey() {
    return 'brilinton.myb.prospect-session.v1';
  }

  prospectSessionProductsKey() {
    return 'brilinton.myb.prospect-session-products.v1';
  }

  prospectPromptedKey() {
    return 'brilinton.myb.prospect-prompted.v2';
  }

  prospectPurchaseIntentKey() {
    return 'brilinton.myb.purchase-intent.v2';
  }

  prospectInterestKey() {
    return 'brilinton.myb.meaningful-interest.v2';
  }

  prospectLastPageKey() {
    return 'brilinton.myb.last-page.v2';
  }

  prospectNotificationShownKey() {
    return 'brilinton.myb.notification-shown.v2';
  }

  prospectNotificationDismissedKey() {
    return 'brilinton.myb.notification-dismissed.v2';
  }

  prospectPurchaseResumeKey() {
    return 'brilinton.myb.purchase-resume.v2';
  }

  readProspectState() {
    const fallback = { version: 1, sessionCount: 0, lastSessionAt: 0, dismissedUntil: 0, products: [] };
    try {
      const raw = window.localStorage.getItem(this.prospectStorageKey());
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      const products = Array.isArray(parsed?.products) ? parsed.products : [];
      return {
        version: 1,
        sessionCount: Math.max(0, Number(parsed?.sessionCount || 0)),
        lastSessionAt: Math.max(0, Number(parsed?.lastSessionAt || 0)),
        dismissedUntil: Math.max(0, Number(parsed?.dismissedUntil || 0)),
        products,
      };
    } catch (_error) {
      return fallback;
    }
  }

  writeProspectState() {
    if (!this.prospectState) return;
    try {
      window.localStorage.setItem(this.prospectStorageKey(), JSON.stringify(this.prospectState));
    } catch (_error) {}
  }

  sessionValue(key) {
    try { return window.sessionStorage.getItem(key); } catch (_error) { return null; }
  }

  setSessionValue(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (_error) {}
  }

  currentPageType() {
    return String(this.dataset.pageType || '').trim().toLowerCase();
  }

  navigationType() {
    try { return performance.getEntriesByType('navigation')[0]?.type || ''; } catch (_error) { return ''; }
  }

  readPreviousPage() {
    try {
      const raw = window.sessionStorage.getItem(this.prospectLastPageKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.pageType || !parsed.path) return null;
      return parsed;
    } catch (_error) {
      return null;
    }
  }

  writeCurrentPageSnapshot() {
    const snapshot = {
      pageType: this.currentPageType(),
      path: window.location.pathname,
      productHandle: String(this.dataset.currentProductHandle || ''),
      meaningful: Boolean(this.prospectMeaningfulCurrent),
      at: Date.now(),
    };
    try { window.sessionStorage.setItem(this.prospectLastPageKey(), JSON.stringify(snapshot)); } catch (_error) {}
  }

  previousPageMatchesReferrer() {
    if (!this.prospectPreviousPage?.path || !document.referrer) return false;
    try {
      const referrer = new URL(document.referrer, window.location.origin);
      return referrer.origin === window.location.origin && referrer.pathname === this.prospectPreviousPage.path;
    } catch (_error) {
      return false;
    }
  }

  isBroadDiscoveryPage() {
    return ['collection', 'index', 'search'].includes(this.currentPageType());
  }

  initializeProspectCapture() {
    // Product history and Cart recovery remain available when signup prompts
    // are paused. The invitation setting controls only automatic acquisition.
    if (this.classList.contains('is-signed-in')) return;

    const now = Date.now();
    this.prospectState = this.readProspectState();
    const retentionMs = 30 * 24 * 60 * 60 * 1000;
    this.prospectState.products = this.prospectState.products
      .filter((item) => item && item.handle && Number(item.lastViewedAt || 0) > now - retentionMs)
      .slice(0, 8);

    const existingSessionStart = this.sessionValue(this.prospectSessionKey());
    const isNewBrowserSession = !existingSessionStart;
    const priorSessionCount = Math.max(0, Number(this.prospectState.sessionCount || 0));
    const priorVisitAt = Math.max(0, Number(this.prospectState.lastSessionAt || 0));
    const returnGapMs = 30 * 60 * 1000;
    let sameSiteReferrer = false;
    if (document.referrer) {
      try { sameSiteReferrer = new URL(document.referrer, window.location.origin).origin === window.location.origin; } catch (_error) {}
    }
    const hasPriorVisit = priorSessionCount >= 1 && priorVisitAt > 0;
    const isDistinctReturnVisit = isNewBrowserSession
      && hasPriorVisit
      && (!sameSiteReferrer || now - priorVisitAt >= returnGapMs);

    if (isNewBrowserSession) {
      this.setSessionValue(this.prospectSessionKey(), String(now));
      if (!hasPriorVisit || isDistinctReturnVisit) this.prospectState.sessionCount = priorSessionCount + 1;
    }

    this.prospectIsReturningSession = isDistinctReturnVisit && this.prospectState.sessionCount >= 2;
    this.prospectState.lastSessionAt = now;
    this.prospectSessionStartedAt = Number(this.sessionValue(this.prospectSessionKey())) || now;
    this.prospectPreviousPage = this.readPreviousPage();

    this.captureCurrentProduct();
    this.updateProspectScrollDepth();

    const hasSessionInterest = this.sessionValue(this.prospectInterestKey()) === '1';
    const previousWasProduct = this.prospectPreviousPage?.pageType === 'product';
    this.prospectDriftCandidate = !isNewBrowserSession
      && previousWasProduct
      && (this.previousPageMatchesReferrer() || this.navigationType() === 'back_forward')
      && this.isBroadDiscoveryPage()
      && hasSessionInterest;
    this.prospectReturnCandidate = this.prospectIsReturningSession;

    const cartCount = Number(this.dataset.cartCount || '0');
    if (cartCount > 0) {
      this.markPurchaseIntent();
      if (this.prospectIsReturningSession) {
        window.__brilintonPrimarySurfacePriority = 'cart';
        this.schedulePurchaseResume();
      }
    } else if (this.prospectEnabled && !this.hasActiveBenefit() && !this.prospectIsSuppressed() && this.prospectReturnCandidate) {
      window.__brilintonPrimarySurfacePriority = 'my-brilinton';
      this.scheduleProspectPriorityRelease();
    } else if (this.prospectEnabled && !this.hasActiveBenefit() && !this.prospectIsSuppressed() && this.prospectDriftCandidate) {
      window.__brilintonPrimarySurfacePriority = 'my-brilinton';
      this.scheduleProspectPriorityRelease();
    }

    this.writeProspectState();
    this.writeCurrentPageSnapshot();
    this.renderProspectRecommendations();

    window.addEventListener('scroll', this.handleProspectScroll, { passive: true });
    document.addEventListener('click', this.handleProspectInteraction, true);
    document.addEventListener('change', this.handleProspectInteraction, true);
    document.addEventListener('brilinton:surface-open', this.handleSurfaceOpen);
    document.addEventListener('brilinton:surface-close', this.handleSurfaceClose);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      this.prospectCartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const itemCount = Number(event?.cartData?.item_count);
        if (Number.isFinite(itemCount)) this.dataset.cartCount = String(Math.max(0, itemCount));
        if (Number.isFinite(itemCount) && itemCount <= 0) {
          this.setSessionValue(this.prospectPurchaseIntentKey(), '0');
          if (this.prospectEnabled) window.setTimeout(() => this.evaluateProspectPrompt(), 250);
          return;
        }
        this.markPurchaseIntent();
      });
    }

    this.prospectInterval = window.setInterval(() => {
      if (this.prospectEnabled) this.tickProspectNotification();
      if (document.visibilityState !== 'visible' || !this.hasCurrentProspectProduct()) return;
      this.prospectVisibleSeconds += 1;
      this.maybeMarkCurrentProductMeaningful();
      if (this.prospectEnabled && this.prospectVisibleSeconds % 2 === 0) this.evaluateProspectPrompt();
    }, 1000);

    if (this.prospectEnabled) {
      window.setTimeout(() => this.evaluateProspectPrompt(), Math.max(250, this.prospectReturnDelay * 1000));
    }
  }

  scheduleProspectPriorityRelease() {
    window.setTimeout(() => {
      if (this.offerEligible || this.classList.contains('active')) return;
      if (window.__brilintonPrimarySurfacePriority !== 'my-brilinton') return;
      window.__brilintonPrimarySurfacePriority = '';
      document.dispatchEvent(new CustomEvent('brilinton:primary-priority-clear', { detail: { name: 'my-brilinton' } }));
    }, 3500);
  }

  schedulePurchaseResume() {
    if (this.sessionValue(this.prospectPurchaseResumeKey()) === '1') return;

    let attempts = 0;
    const openCart = () => {
      attempts += 1;
      if (this.sessionValue(this.prospectPurchaseResumeKey()) === '1') return;
      if (window.BrilintonSurfaceManager?.top?.() && !window.BrilintonSurfaceManager.isOpen?.('cart')) {
        if (attempts < 10) window.setTimeout(openCart, 250);
        return;
      }

      const drawer = document.querySelector('cart-drawer');
      const trigger = document.querySelector('#cart-icon-bubble');
      if (drawer && typeof drawer.open === 'function') {
        this.setSessionValue(this.prospectPurchaseResumeKey(), '1');
        this.trackClarityEvent('purchase_resume_cart_opened');
        if (!drawer.classList.contains('active')) drawer.open(trigger);
        return;
      }

      if (attempts < 10) window.setTimeout(openCart, 250);
    };

    window.setTimeout(openCart, 550);
  }

  hasCurrentProspectProduct() {
    return Boolean(this.dataset.currentProductHandle);
  }

  captureCurrentProduct() {
    const handle = String(this.dataset.currentProductHandle || '').trim();
    if (!handle || !this.prospectState) return;

    let sessionProducts = [];
    try {
      const raw = window.sessionStorage.getItem(this.prospectSessionProductsKey());
      sessionProducts = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(sessionProducts)) sessionProducts = [];
    } catch (_error) {}

    if (!sessionProducts.includes(handle)) {
      sessionProducts.push(handle);
      try { window.sessionStorage.setItem(this.prospectSessionProductsKey(), JSON.stringify(sessionProducts.slice(-8))); } catch (_error) {}
    }

    const navigationEntry = typeof performance !== 'undefined' && performance.getEntriesByType
      ? performance.getEntriesByType('navigation')[0]
      : null;
    const isReload = navigationEntry?.type === 'reload';
    const now = Date.now();
    const existingIndex = this.prospectState.products.findIndex((item) => item.handle === handle);
    const existing = existingIndex >= 0 ? this.prospectState.products[existingIndex] : null;
    const currentUrl = this.currentProspectProductUrl();
    const item = {
      id: String(this.dataset.currentProductId || existing?.id || ''),
      handle,
      title: String(this.dataset.currentProductTitle || existing?.title || '').trim(),
      productType: String(this.dataset.currentProductType || existing?.productType || '').trim(),
      url: currentUrl || String(this.dataset.currentProductUrl || existing?.url || ''),
      image: String(this.dataset.currentProductImage || existing?.image || '').trim(),
      available: this.dataset.currentProductAvailable !== 'false',
      lastViewedAt: now,
      viewCount: Math.max(1, Number(existing?.viewCount || 0) + (isReload ? 0 : 1)),
      engagementScore: Math.max(0, Number(existing?.engagementScore || 0)),
      engagedAt: Math.max(0, Number(existing?.engagedAt || 0)),
    };

    if (existingIndex >= 0) this.prospectState.products.splice(existingIndex, 1);
    this.prospectState.products.unshift(item);
    this.prospectState.products = this.prospectState.products.slice(0, 8);
  }

  currentProspectProductUrl() {
    const source = new URL(window.location.href);
    const allowed = ['variant', 'iphone_device', 'size', 'model', 'wear', 'set', 'device', 'mode', 'frame_type', 'frame', 'band'];
    const params = new URLSearchParams();
    allowed.forEach((key) => {
      const value = source.searchParams.get(key);
      if (value) params.set(key, value);
    });
    const query = params.toString();
    return `${source.pathname}${query ? `?${query}` : ''}`;
  }

  handleProspectScroll() {
    this.updateProspectScrollDepth();
    this.maybeMarkCurrentProductMeaningful();
    if (this.notification?.classList.contains('is-visible')) {
      this.updateNotificationOffset();
      this.notificationScrollDistance = Math.max(this.notificationScrollDistance, Math.abs((window.scrollY || 0) - this.notificationStartY));
      this.maybeCollapseIgnoredNotification();
    }
  }

  updateProspectScrollDepth() {
    const scrollHeight = Math.max(document.documentElement.scrollHeight || 0, document.body?.scrollHeight || 0);
    if (!scrollHeight) return;
    const depth = Math.min(1, Math.max(0, ((window.scrollY || 0) + window.innerHeight) / scrollHeight));
    this.prospectMaxScrollDepth = Math.max(this.prospectMaxScrollDepth, depth);
  }

  handleProspectInteraction(event) {
    if (!this.hasCurrentProspectProduct()) return;
    const purchaseControl = event.target?.closest?.(
      'product-form [name="add"], product-form button[type="submit"], .product-form__submit, .shopify-payment-button__button'
    );
    if (purchaseControl) {
      this.markPurchaseIntent();
      return;
    }
    const target = event.target?.closest?.(
      'media-gallery, variant-selects, .product-form__input, .product-faq__trigger, .product__accordion summary, .pdp-studio-switch, product-form, .product__media-wrapper button, .product__info-container button, .product__info-container input, .product__info-container select'
    );
    if (!target) return;
    if (target.closest?.('[data-my-brilinton-notification], my-brilinton-drawer')) return;
    this.prospectInteractionCount += 1;
    if (this.notification?.classList.contains('is-visible') && Date.now() - this.notificationLastActionAt >= 600) {
      this.notificationActions += 1;
      this.notificationLastActionAt = Date.now();
      this.maybeCollapseIgnoredNotification();
    }
    this.maybeMarkCurrentProductMeaningful();
    this.evaluateProspectPrompt();
  }

  maybeMarkCurrentProductMeaningful() {
    if (this.prospectMeaningfulCurrent || !this.hasCurrentProspectProduct() || !this.prospectState) return;
    if (this.prospectVisibleSeconds < this.prospectEngagedSeconds) return;

    const hasAction = this.prospectInteractionCount > 0;
    const hasDepth = this.prospectMaxScrollDepth >= 0.45;
    if (!hasAction && !hasDepth) return;

    this.prospectMeaningfulCurrent = true;
    this.setSessionValue(this.prospectInterestKey(), '1');

    const handle = String(this.dataset.currentProductHandle || '');
    const product = this.prospectState.products.find((item) => item.handle === handle);
    if (product) {
      product.engagementScore = Math.max(0, Number(product.engagementScore || 0)) + 1;
      product.engagedAt = Date.now();
    }

    this.writeProspectState();
    this.writeCurrentPageSnapshot();
    this.trackClarityEvent('myb_meaningful_pdp_interest');
  }

  pdpNotificationEligible() {
    if (!this.hasCurrentProspectProduct()) return false;
    if (this.sessionValue(this.prospectInterestKey()) !== '1') return false;
    if (this.prospectVisibleSeconds < this.prospectEngagedSeconds) return false;
    return this.prospectInteractionCount > 0 || this.prospectMaxScrollDepth >= 0.35;
  }

  handlePageShow(event) {
    if (!event?.persisted || this.classList.contains('is-signed-in')) return;

    this.prospectState = this.readProspectState();
    this.prospectPreviousPage = this.readPreviousPage();
    const hasSessionInterest = this.sessionValue(this.prospectInterestKey()) === '1';
    const restoredDrift = this.isBroadDiscoveryPage()
      && this.prospectPreviousPage?.pageType === 'product'
      && hasSessionInterest;

    if (!restoredDrift) {
      this.writeCurrentPageSnapshot();
      return;
    }

    this.prospectDriftCandidate = true;
    this.prospectReturnCandidate = false;
    this.renderProspectRecommendations();
    this.writeCurrentPageSnapshot();

    if (!this.prospectEnabled || this.sessionValue(this.prospectPurchaseIntentKey()) === '1' || this.hasActiveBenefit() || this.prospectIsSuppressed()) return;
    window.__brilintonPrimarySurfacePriority = 'my-brilinton';
    this.scheduleProspectPriorityRelease();
    window.setTimeout(() => this.evaluateProspectPrompt(), 80);
  }

  handlePageHide() {
    this.hideProspectNotification({ keepBadge: true });
  }

  handleVisibilityChange() {
    // Hidden-tab time never counts as exposure, including browser throttling.
    this.notificationLastTick = performance.now();
  }

  handleSurfaceOpen(event) {
    const name = String(event?.detail?.name || '');
    if (name === 'cart-drawer' || name === 'cart') {
      // Opening an empty bag is a surface conflict, not purchase intent.
      // Keep real cart contents and an active add-to-cart attempt authoritative.
      if (Number(this.dataset.cartCount || '0') > 0 || this.sessionValue(this.prospectPurchaseIntentKey()) === '1') {
        this.markPurchaseIntent();
      } else {
        this.hideProspectNotification({ keepBadge: true });
      }
      return;
    }
    if (name && name !== 'my-brilinton') this.hideProspectNotification({ keepBadge: true });
  }

  handleSurfaceClose() {
    if (window.BrilintonSurfaceManager?.top?.()) return;
    window.setTimeout(() => this.evaluateProspectPrompt(), 120);
  }

  handleProspectNotificationOpen(event) {
    event?.preventDefault?.();
    if (!this.offerEligible || this.hasActiveBenefit()) return;
    this.setSessionValue(this.prospectPromptedKey(), '1');
    this.hideProspectNotification({ keepBadge: false });
    this.trackClarityEvent('myb_interest_notification_opened');
    this.open(this.triggers?.[0], { reason: 'notification' });
  }

  handleProspectNotificationClose(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.setSessionValue(this.prospectNotificationDismissedKey(), '1');
    this.setSessionValue(this.prospectPromptedKey(), '1');
    this.hideProspectNotification({ keepBadge: false });
    this.triggers?.[0]?.focus({ preventScroll: true });
    this.trackClarityEvent('myb_interest_notification_dismissed');
  }

  showProspectNotification() {
    if (!this.prospectEnabled || !this.notification || !this.notificationOpen) return;
    if (this.sessionValue(this.prospectNotificationShownKey()) === '1') return;
    if (this.sessionValue(this.prospectNotificationDismissedKey()) === '1') return;
    if (this.sessionValue(this.prospectPromptedKey()) === '1') return;
    if (this.sessionValue(this.prospectPurchaseIntentKey()) === '1') return;
    if (window.BrilintonSurfaceManager?.top?.() || document.querySelector('header-drawer details[open]')) return;

    this.setSessionValue(this.prospectNotificationShownKey(), '1');
    this.notificationExposureMs = 0;
    this.notificationActions = 0;
    this.notificationLastActionAt = Date.now();
    this.notificationStartY = window.scrollY || 0;
    this.notificationScrollThreshold = Math.max(1, window.innerHeight) * 2;
    this.notificationScrollDistance = 0;
    this.notificationLastTick = performance.now();
    this.updateNotificationOffset();
    this.notification.hidden = false;
    this.notification.classList.add('is-visible');
    this.setNoticeBadge(true);
    this.trackClarityEvent('myb_interest_notification_shown');
  }

  tickProspectNotification() {
    const now = performance.now();
    const elapsed = Math.min(1000, Math.max(0, now - this.notificationLastTick));
    this.notificationLastTick = now;
    if (document.visibilityState !== 'visible' || !this.notification?.classList.contains('is-visible')) return;
    if (window.BrilintonSurfaceManager?.top?.() || document.querySelector('header-drawer details[open]')) {
      this.hideProspectNotification({ keepBadge: true });
      return;
    }
    const rect = this.notification.getBoundingClientRect();
    if (rect.height > 0 && rect.top >= 0 && rect.bottom <= window.innerHeight) this.notificationExposureMs += elapsed;
    this.maybeCollapseIgnoredNotification();
  }

  maybeCollapseIgnoredNotification() {
    if (!this.notification?.classList.contains('is-visible') || document.visibilityState !== 'visible') return;
    if (this.notificationExposureMs < this.prospectNotificationSeconds * 1000) return;
    if (this.notification.contains(document.activeElement)) return;
    if (window.matchMedia('(hover: hover)').matches && this.notification.matches(':hover')) return;
    if (this.notificationScrollDistance < this.notificationScrollThreshold && this.notificationActions < 3) return;
    this.hideProspectNotification({ keepBadge: true });
    this.trackClarityEvent('myb_interest_notification_ignored');
  }

  hideProspectNotification({ keepBadge = true } = {}) {
    if (this.notification) {
      this.notification.classList.remove('is-visible');
      window.setTimeout(() => {
        if (this.notification && !this.notification.classList.contains('is-visible')) this.notification.hidden = true;
      }, 180);
    }
    if (!keepBadge) this.setNoticeBadge(false);
  }

  markPurchaseIntent() {
    this.setSessionValue(this.prospectPurchaseIntentKey(), '1');
    this.hideProspectNotification({ keepBadge: false });
    this.setNoticeBadge(false);
  }

  prospectIsSuppressed() {
    return Boolean(this.prospectState?.dismissedUntil && this.prospectState.dismissedUntil > Date.now());
  }

  suppressProspectPrompt() {
    if (!this.prospectState) return;
    this.prospectState.dismissedUntil = Date.now() + (this.prospectDismissDays * 24 * 60 * 60 * 1000);
    this.writeProspectState();
    this.hideProspectNotification({ keepBadge: false });
    this.setNoticeBadge(false);
    this.trackClarityEvent('myb_prospect_prompt_dismissed');
  }

  currentProspectReason() {
    if (!this.prospectState) return '';
    const elapsedSeconds = Math.max(0, (Date.now() - this.prospectSessionStartedAt) / 1000);
    if (this.prospectReturnCandidate && elapsedSeconds >= this.prospectReturnDelay) return 'return';
    if (!this.prospectState.products.length) return '';
    if (this.prospectDriftCandidate) return 'drift';
    if (this.pdpNotificationEligible()) return 'pdp_interest';
    return '';
  }

  canAutoOpenProspect() {
    if (!this.prospectEnabled || !this.offerEligible || this.hasActiveBenefit() || this.submittedThisPage) return false;
    if (this.classList.contains('active')) return false;
    if (this.prospectIsSuppressed()) return false;
    if (this.sessionValue(this.prospectPromptedKey()) === '1') return false;
    if (this.sessionValue(this.prospectPurchaseIntentKey()) === '1') return false;
    if (window.BrilintonSurfaceManager?.top?.()) return false;
    if (document.querySelector('header-drawer details[open]')) return false;
    return true;
  }

  evaluateProspectPrompt() {
    if (!this.prospectEnabled || this.classList.contains('is-signed-in')) return;
    if (!this.offerEligible || this.hasActiveBenefit() || this.prospectIsSuppressed()) return;
    if (this.sessionValue(this.prospectPurchaseIntentKey()) === '1') return;
    if (this.sessionValue(this.prospectPromptedKey()) === '1' || this.sessionValue(this.prospectNotificationDismissedKey()) === '1') return;

    const reason = this.currentProspectReason();
    if (!reason) return;
    const products = this.prospectProductsForDisplay();
    if (reason !== 'return' && !products.length) return;
    this.renderProspectRecommendations();

    if (reason === 'pdp_interest') {
      this.setNoticeBadge(true);
      this.showProspectNotification();
      return;
    }

    if (!this.canAutoOpenProspect()) {
      if (this.sessionValue(this.prospectPromptedKey()) !== '1') {
        this.setNoticeBadge(true);
      }
      return;
    }

    this.setSessionValue(this.prospectPromptedKey(), '1');
    this.trackClarityEvent(`myb_prospect_prompt_${reason}`);
    this.open(this.triggers?.[0], { reason: 'prospect' });
  }

  prospectProductsForDisplay() {
    if (!this.prospectState?.products?.length) return [];
    const currentHandle = String(this.dataset.currentProductHandle || '');
    return this.prospectState.products
      .filter((item) => item && item.available !== false && item.handle && item.title && item.url)
      .sort((a, b) => {
        const aCurrent = a.handle === currentHandle ? 1 : 0;
        const bCurrent = b.handle === currentHandle ? 1 : 0;
        if (aCurrent !== bCurrent) return bCurrent - aCurrent;
        if (Number(a.engagementScore || 0) !== Number(b.engagementScore || 0)) {
          return Number(b.engagementScore || 0) - Number(a.engagementScore || 0);
        }
        if (Number(a.viewCount || 0) !== Number(b.viewCount || 0)) return Number(b.viewCount || 0) - Number(a.viewCount || 0);
        return Number(b.lastViewedAt || 0) - Number(a.lastViewedAt || 0);
      })
      .slice(0, this.prospectMaxProducts);
  }

  renderProspectRecommendations() {
    if (!this.prospectSection || !this.prospectProducts || this.classList.contains('is-signed-in')) return;
    if (this.hasActiveBenefit() || this.submittedThisPage) {
      this.destroyProspectSlider();
      this.prospectSection.hidden = true;
      return;
    }
    const products = this.prospectProductsForDisplay();
    const signature = JSON.stringify(products.map(({ handle, title, productType, url, image }) => ({ handle, title, productType, url, image })));
    // Eligibility checks run repeatedly. Preserve the rail's scroll position,
    // DOM focus and loaded images while its product selection is unchanged.
    if (signature === this.prospectRenderSignature) return;
    this.prospectRenderSignature = signature;
    this.destroyProspectSlider();
    this.prospectProducts.replaceChildren();

    if (!products.length) {
      this.prospectSection.hidden = true;
      return;
    }

    products.forEach((product) => {
      const slide = document.createElement('div');
      slide.className = 'my-brilinton-drawer__prospect-slide product-card-wrapper--collection';
      slide.setAttribute('role', 'listitem');
      const card = document.createElement('a');
      card.className = 'my-brilinton-drawer__prospect-card';
      card.href = product.url;
      card.draggable = false;
      card.addEventListener('click', () => {
        if (!this.prospectFlickity?.isPreventingClicks) this.trackClarityEvent('myb_prospect_product_clicked');
      });

      const media = document.createElement('span');
      media.className = 'my-brilinton-drawer__prospect-media';
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = product.title;
        img.loading = 'lazy';
        img.draggable = false;
        img.width = 320;
        img.height = 360;
        media.appendChild(img);
      } else {
        const fallback = document.createElement('span');
        fallback.textContent = 'BRILINTON';
        media.appendChild(fallback);
      }

      const copy = document.createElement('div');
      copy.className = 'my-brilinton-drawer__prospect-copy';
      if (product.productType) {
        const type = document.createElement('p');
        type.className = 'product-inner type';
        type.textContent = product.productType;
        copy.appendChild(type);
      }
      // Collection cards use the name before the finish separator and omit
      // WatchWare from the mobile line. Keep the full title on image alt text.
      const title = document.createElement('h3');
      title.className = 'card__heading h5';
      const name = product.title.split('-')[0].trim();
      const mobileTitle = document.createElement('span');
      mobileTitle.className = 'my-brilinton-drawer__prospect-title-mobile';
      mobileTitle.textContent = name.replace('WatchWare', '').trim();
      const desktopTitle = document.createElement('span');
      desktopTitle.className = 'my-brilinton-drawer__prospect-title-desktop';
      desktopTitle.textContent = name;
      title.append(mobileTitle, desktopTitle);
      copy.appendChild(title);
      card.append(media, copy);
      slide.appendChild(card);
      this.prospectProducts.appendChild(slide);
    });

    this.prospectSection.hidden = false;
    this.refreshProspectSlider();
  }

  loadProspectFlickity() {
    if (window.Flickity) return Promise.resolve(window.Flickity);
    // Reuse the asset router's pinned vendor. Other page types load it only
    // when an open drawer actually has more cards than fit in the rail.
    if (!MyBrilintonDrawer.flickityReady) {
      MyBrilintonDrawer.flickityReady = new Promise((resolve) => {
        const src = 'https://unpkg.com/flickity@2.3.0/dist/flickity.pkgd.min.js';
        let script = document.querySelector(`script[src="${src}"]`);
        const existing = Boolean(script);
        if (!script) {
          script = document.createElement('script');
          script.src = src;
          script.async = true;
        }
        const complete = () => {
          script.removeEventListener('load', complete);
          script.removeEventListener('error', complete);
          resolve(window.Flickity || null);
        };
        script.addEventListener('load', complete);
        script.addEventListener('error', complete);
        if (!existing) document.head.appendChild(script);
        else if (document.readyState === 'complete') complete();
      });
    }
    return MyBrilintonDrawer.flickityReady;
  }

  refreshProspectSlider() {
    const rail = this.prospectProducts;
    if (!this.isConnected || !this.classList.contains('active') || !rail || this.prospectSection.hidden || !rail.clientWidth) return;
    const cells = Array.from(rail.querySelectorAll('.my-brilinton-drawer__prospect-slide'));
    const contentWidth = cells.reduce((width, cell) => width + cell.getBoundingClientRect().width + (parseFloat(getComputedStyle(cell).marginRight) || 0), 0);
    if (contentWidth <= rail.clientWidth + 1) {
      this.destroyProspectSlider();
      return;
    }
    if (!window.Flickity) {
      if (this.prospectFlickityLoading) return;
      this.prospectFlickityLoading = true;
      this.loadProspectFlickity().then((Flickity) => {
        this.prospectFlickityLoading = false;
        if (Flickity) this.refreshProspectSlider();
      });
      return;
    }
    if (this.prospectFlickity) {
      this.prospectFlickity.resize();
      return;
    }
    rail.scrollLeft = 0;
    rail.setAttribute('role', 'region');
    rail.setAttribute('aria-roledescription', 'carousel');
    this.prospectFlickity = new window.Flickity(rail, {
      cellSelector: '.my-brilinton-drawer__prospect-slide',
      cellAlign: 'left',
      contain: true,
      groupCells: true,
      wrapAround: false,
      pageDots: false,
      prevNextButtons: true,
      draggable: true,
      accessibility: true,
      percentPosition: true,
      resize: false,
    });
    this.prospectFlickity.slider.setAttribute('role', 'list');
    this.prospectFlickity.on('select', this.syncProspectSliderFocus);
    this.syncProspectSliderFocus();
  }

  syncProspectSliderFocus() {
    this.prospectFlickity?.cells.forEach(({ element }) => {
      element.querySelector('a').tabIndex = element.classList.contains('is-selected') ? 0 : -1;
    });
  }

  handleProspectCardFocus(event) {
    const cell = event.target.closest('.my-brilinton-drawer__prospect-slide');
    if (cell && !cell.classList.contains('is-selected')) this.prospectFlickity?.selectCell(cell, false, true);
  }

  destroyProspectSlider() {
    if (!this.prospectFlickity) return;
    this.prospectFlickity.destroy();
    this.prospectFlickity = null;
    this.prospectProducts.setAttribute('role', 'list');
    this.prospectProducts.removeAttribute('aria-roledescription');
    this.prospectProducts.tabIndex = 0;
    this.prospectProducts.querySelectorAll('.my-brilinton-drawer__prospect-card').forEach((card) => card.removeAttribute('tabindex'));
  }

  benefitStorageKey() {
    return this.formId ? `my-brilinton-member-benefit:${this.formId}` : '';
  }

  benefitDurationMs() {
    return this.benefitDays * 24 * 60 * 60 * 1000;
  }

  readBenefitState() {
    if (!this.formId) return null;

    try {
      const raw = window.localStorage.getItem(this.benefitStorageKey());
      if (!raw) return null;
      const state = JSON.parse(raw);
      const submittedAt = Number(state?.submittedAt || 0);
      if (!submittedAt || Date.now() - submittedAt > this.benefitDurationMs()) {
        window.localStorage.removeItem(this.benefitStorageKey());
        return null;
      }
      return { submittedAt };
    } catch (error) {
      return null;
    }
  }

  hasActiveBenefit() {
    return Boolean(this.readBenefitState());
  }

  restoreBenefitState() {
    if (!this.hasActiveBenefit()) return;
    this.offerEligible = false;
    this.benefitRestored = true;
    this.setNoticeBadge(false);
    this.classList.add('has-member-benefit');

    window.setTimeout(() => {
      if (!this.submittedThisPage) this.showBenefitFallback();
    }, 300);
  }

  activateBenefit() {
    if (this.submittedThisPage) return;
    this.submittedThisPage = true;
    this.offerEligible = false;
    this.benefitRestored = true;
    this.setNoticeBadge(false);
    this.couponWaitUntil = Date.now() + 3500;
    this.classList.add('has-member-benefit', 'is-klaviyo-submitted');
    this.trackClarityEvent('member_benefit_submitted');
    this.prospectPromptActive = false;
    this.setSessionValue(this.prospectPromptedKey(), '1');

    try {
      window.localStorage.setItem(this.benefitStorageKey(), JSON.stringify({ submittedAt: Date.now() }));
    } catch (error) {
      // The current page can still show the confirmation state.
    }

    // Keep Klaviyo's Success step visible while its unique coupon renders.
    // Only use the Brilinton fallback if no coupon block appears after the wait.
    this.showKlaviyoPendingSuccess();
    window.setTimeout(() => this.syncBenefitPresentation(), 250);
    window.setTimeout(() => this.syncBenefitPresentation(), 900);
    window.setTimeout(() => this.syncBenefitPresentation(), 1800);
    window.setTimeout(() => this.syncBenefitPresentation(), 3600);
  }

  hasKlaviyoCouponSuccess() {
    if (!this.klaviyoMount) return false;

    const couponElement = this.klaviyoMount.querySelector(
      '[data-testid*="coupon" i], [class*="coupon" i], [data-coupon], button[aria-label*="copy" i]'
    );
    if (couponElement) return true;

    const text = (this.klaviyoMount.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
    return text.includes('COPY CODE') || text.includes('COUPON CODE') || text.includes('DISCOUNT CODE');
  }

  syncBenefitPresentation() {
    if (!this.hasActiveBenefit()) return;

    if (this.submittedThisPage && this.hasKlaviyoCouponSuccess()) {
      this.showKlaviyoCouponSuccess();
      return;
    }

    if (this.submittedThisPage && Date.now() < this.couponWaitUntil) {
      this.showKlaviyoPendingSuccess();
      return;
    }

    this.showBenefitFallback();
  }

  showKlaviyoPendingSuccess() {
    this.destroyProspectSlider();
    this.setNoticeBadge(false);
    this.classList.add('has-member-benefit', 'is-klaviyo-submitted');
    this.classList.remove('is-klaviyo-eligible', 'is-klaviyo-coupon-success');
    if (this.defaultAuth) this.defaultAuth.hidden = true;
    if (this.offer) this.offer.hidden = false;
    if (this.benefitActive) this.benefitActive.hidden = true;
    if (this.prospectSection) this.prospectSection.hidden = true;
  }

  showKlaviyoCouponSuccess() {
    this.destroyProspectSlider();
    this.setNoticeBadge(false);
    this.classList.add('has-member-benefit', 'is-klaviyo-submitted', 'is-klaviyo-coupon-success');
    this.classList.remove('is-klaviyo-eligible');
    if (this.defaultAuth) this.defaultAuth.hidden = true;
    if (this.offer) this.offer.hidden = false;
    if (this.benefitActive) this.benefitActive.hidden = true;
    if (this.prospectSection) this.prospectSection.hidden = true;
  }

  showBenefitFallback() {
    this.destroyProspectSlider();
    this.setNoticeBadge(false);
    this.classList.add('has-member-benefit');
    this.classList.remove('is-klaviyo-eligible', 'is-klaviyo-coupon-success');
    if (this.defaultAuth) this.defaultAuth.hidden = true;
    if (this.offer) this.offer.hidden = true;
    if (this.benefitActive) this.benefitActive.hidden = false;
    if (this.prospectSection) this.prospectSection.hidden = true;
  }

  reopenAfterAuthentication() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('myb') !== 'open') return;

    url.searchParams.delete('myb');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    window.setTimeout(() => this.open(this.triggers[0]), 180);
  }

  handleKeyup(event) {
    if (event.code === 'Escape') this.close();
  }
}

if (!customElements.get('my-brilinton-drawer')) {
  customElements.define('my-brilinton-drawer', MyBrilintonDrawer);
}
